import { action, computed, observable } from "mobx";
import locale from "@bonliva-crm/locale";
import { compact, debounce, isEmpty } from "lodash";
import {
  CustomerType,
  CustomerContactPersonForm_Assign as assignToCustomer,
  CustomerContactPersonForm_CustomerFragment as Customer,
  CustomerContactPersonForm_GetCustomer as getCustomer,
  CustomerContactPersonFragment as ContactPerson,
  CustomerContactPersonForm_FindContactPersons as findContactPersons,
  CustomerProfile_GetProfile
} from "@graphql";
import { store } from "@store";
import { Navigation } from "@navigation";
import {
  ContactPersonFormStore,
  FormData,
  blockId
} from "@containers/ContactPersonEditor";
import { OkCancelDialogOptions } from "@containers/ConfirmDialog";
import { MutationFetchPolicy } from "@apollo/client/core/watchQueryOptions";

export class CustomerContactPersonFormStore {
  customerData: Customer;
  formStore: ContactPersonFormStore;

  @observable contactPersons: ContactPerson[] = [];
  @observable isLoadingContactPersons = false;
  @observable loading = true;

  @computed get canSubmit() {
    return this.formStore.form.canSubmit;
  }

  @computed get dirty() {
    return this.formStore.form.dirty;
  }

  lookupDebounced: () => void;

  constructor(
    private customerId: string,
    private contactPersonId: string | null,
    private initialData: Partial<FormData>,
    private navigation: Navigation
  ) {
    this.lookupDebounced = debounce(this.lookup, 400);

    this.fetchCustomer()
      .then(this.initForm)
      .finally(() => {
        this.loading = false;
      });
  }

  fetchCustomer = () => {
    return getCustomer
      .func({ customerId: this.customerId }, { fetchPolicy: "network-only" })
      .then(({ data }) => {
        this.customerData = data.customerById;
      })
      .catch((err) => {
        store().snackbars.error(`Cannot load customer\nReason: ${err.toString()}`);
      });
  };

  initForm = () => {
    if (!this.customerData) {
      throw new Error("missing customer data");
    }

    this.formStore = new ContactPersonFormStore(
      this.contactPersonId,
      this.customerId,
      {
        Customers: [{ label: this.customerData.Name, value: this.customerData._id }],
        ...this.initialData
      },
      {
        allowedAreasOfExpertise: this.customerData.AreasOfExpertiseNoLimit,
        customerLookupType: CustomerType.Department
      },
      () => this.navClose(),
      {
        refetchQueries: [CustomerProfile_GetProfile.OperationName],
        fetchPolicy: "network-only" as MutationFetchPolicy
      }
    );

    this.formStore.allCustomerGroups = compact(
      this.customerData.DirectChildrenFull.flatMap((x) => x.CustomerGroups)
    ).unique();

    this.formStore.form.onSetValue((options) => {
      if (
        options.name === "Name" ||
        options.name === "Email" ||
        options.name === "Phone"
      ) {
        this.lookupDebounced();
      }
    });
  };

  @action lookup = () => {
    const { values } = this.formStore.form;

    const name = values.Name.trim();
    const phone = values.Phone.trim();
    const email = values.Email.trim();

    const text = compact([name, phone, email]);

    if (text.length === 0) {
      this.contactPersons = [];
      return;
    }

    this.isLoadingContactPersons = true;

    const rootCustomerId = isEmpty(this.customerData.Parents)
      ? this.customerData._id
      : this.customerData.Parents[0].Id;

    findContactPersons
      .func({ text, rootCustomerId, skip: 0, limit: 20 }, { fetchPolicy: "network-only" })
      .then(({ data }) => {
        this.contactPersons = data.contactPersons.items;
      })
      .catch((err) => {
        console.error(err);
      })
      .finally(() => {
        this.isLoadingContactPersons = false;
      });
  };

  private navClose = () => {
    this.navigation.unBlock(blockId);
    this.navigation.close(false);
  };

  assignContactPerson = (contactPersonId: string, name: string) => {
    const dialogOptions = new OkCancelDialogOptions();

    dialogOptions.message = locale.AssignContactPersonToCustomer(
      name,
      this.customerData.Name
    );

    store()
      .ui.confirmDialog.confirm(dialogOptions)
      .then((result) => {
        if (result !== "ok") {
          return;
        }

        this.loading = true;

        assignToCustomer
          .func({ contactPersonId, customerId: this.customerId })
          .then(() => this.navClose())
          .catch((err) => {
            store().snackbars.error(`Cannot assign\nReason: ${err}`);
          })
          .finally(() => {
            this.loading = false;
          });
      });
  };

  submit = () => {
    this.formStore.form.submit();
  };

  reset = () => {
    this.formStore.form.reset(this.formStore.form.initialValues);
  };
}
